/**
 * Pont entre le `MutationCache` de React Query (hors arbre React) et le
 * `ToastProvider` (contexte React).
 *
 * Pourquoi : le QueryClient est créé au chargement du module, bien avant le
 * premier render — il ne peut donc pas appeler `useToast()`. Le composant
 * `MutationErrorToaster` s'enregistre ici au montage, et le MutationCache
 * appelle `reportMutationError` sans rien savoir de React.
 *
 * Contexte (audit 2026-08-07) : aucune mutation n'avait de `onError`, et il
 * n'existait aucun handler global. Quand la base refusait une écriture — cas
 * typique du garde-fou de publication (migration 082) qui bloque le passage
 * en « Publié » si un critère manque — l'utilisateur ne voyait RIEN. Sur la
 * fiche vente, où publier consiste justement à basculer le déroulant
 * `statutPublication`, l'échec était totalement invisible.
 */

type Notifier = (rawMessage: string) => void;

let notifier: Notifier | null = null;

/** Appelé par MutationErrorToaster au montage (et démonté avec `null`). */
export function setMutationErrorNotifier(fn: Notifier | null) {
  notifier = fn;
}

/**
 * Signale une erreur de mutation à l'utilisateur. No-op tant qu'aucun
 * notifier n'est enregistré (ex. erreur survenue avant le premier render, ou
 * dans les tests) — on ne veut jamais qu'un échec d'affichage masque l'erreur
 * d'origine.
 */
export function reportMutationError(error: unknown) {
  const raw = error instanceof Error ? error.message : String(error);
  notifier?.(raw);
}
